import { useEffect, useState } from "react";
import {
  AutocompleteInput,
  CreateButton,
  Datagrid,
  DateField,
  EditButton,
  FunctionField,
  List,
  NumberField,
  ReferenceField,
  ReferenceInput,
  ShowButton,
  SimpleList,
  TextField,
  TextInput,
  Title,
  TopToolbar,
  useDataProvider,
  useNotify,
  useStore,
} from "react-admin";
import { Box, Paper, Typography, useMediaQuery } from "@mui/material";
import { settings } from "app";
import permissions from "app/permissions";
import { getPermissions } from "auth/security/securityUtils";
import { checkPermission } from "server/security/utils";
import SecuredComponent from "server/security/SecuredComponent";
import CustomFilter from "features/CustomFilter";
import { MobileListRow } from "features/MobileListRow";
import { EmptyList } from "server/components";
import { administrationRequests } from "api/administrationApi";
import { studentRequests } from "api/studentApi";
import { GradingTypeOptions } from "utils/choices";
import { MOBILE_BREAKPOINT } from "utils/constants";
import { useNotifyError } from "utils/useNotifyError";

export const ELECTIVE_COURSES_STORE_KEY = "electiveCoursesMF.filters";

const gradingTypeName = gradingType => GradingTypeOptions.find(o => o.id === gradingType)?.name ?? "-";

const ListActions = ({ hasCreate }) => <TopToolbar>{hasCreate && <CreateButton />}</TopToolbar>;

const ListElectiveCourses = () => {
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const notifyError = useNotifyError();

  const [filters, setFilters] = useStore(ELECTIVE_COURSES_STORE_KEY, null);
  const [academicYearId, setAcademicYearId] = useState(filters?.academicYearId ?? null);
  const [academicYearChoices, setAcademicYearChoices] = useState([]);

  useEffect(() => {
    dataProvider
      .customRequest(studentRequests.getAcademicYearsSelect())
      .then(res => setAcademicYearChoices(res.data ?? []))
      .catch(error => notifyError(error));
  }, [dataProvider, notifyError]);

  const onSearch = () => {
    if (!academicYearId) {
      notify("Моля изберете учебна година!", { type: "error" });
      return;
    }
    setFilters({ academicYearId });
  };

  const handleAcademicYearChange = value => {
    setAcademicYearId(value);
    if (!value) {
      setFilters(null);
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      {!filters && <Title title="Свободноизбираеми курсове в МФ" />}
      <CustomFilter
        title="Свободноизбираеми курсове в МФ"
        titleClasses={{ padding: "0 3rem" }}
        filters={[
          {
            label: "Учебна година",
            value: academicYearId,
            setValue: handleAcademicYearChange,
            type: "autocomplete",
            choices: academicYearChoices,
            multiple: false,
          },
        ]}
        buttonLabel="Провери"
        buttonOnClick={onSearch}
        buttonDisabled={!academicYearId}
      />
      {!filters ? (
        <Paper>
          <Box mt={3}>
            <Typography
              variant="inherit"
              align="center"
              color="secondary"
            >
              Изберете учебна година, за да заредите свободноизбираемите курсове.
            </Typography>
          </Box>
        </Paper>
      ) : (
        <List
          title="Свободноизбираеми курсове в МФ"
          filter={filters}
          actions={<ListActions hasCreate={checkPermission(getPermissions(), permissions.canManageElectiveCourses)} />}
        >
          {isMobile ? (
            <SimpleList
              empty={<EmptyList />}
              linkType={false}
              sx={settings.style.mobileList}
              primaryText={record => <MobileListRow fields={[{ label: "Име на модула", value: record?.nameBg }]} />}
              secondaryText={record => (
                <div>
                  <MobileListRow
                    fields={[
                      {
                        label: "Катедра",
                        value: (
                          <ReferenceField
                            source="departmentId"
                            reference={administrationRequests.getDepartmentsSelect()}
                          >
                            <TextField source="fullName" />
                          </ReferenceField>
                        ),
                      },
                    ]}
                  />
                  <MobileListRow
                    fields={[{ label: "Период", value: `${record?.durationFrom} - ${record?.durationTo}` }]}
                  />
                  <MobileListRow fields={[{ label: "Капацитет", value: record?.studentCapacity }]} />
                  <MobileListRow
                    fields={[{ label: "Форма на оценяване", value: gradingTypeName(record?.gradingType) }]}
                  />
                </div>
              )}
              tertiaryText={() => (
                <>
                  <SecuredComponent permission={permissions.canManageElectiveCourses}>
                    <EditButton label="" />
                  </SecuredComponent>
                  <ShowButton label="" />
                </>
              )}
            />
          ) : (
            <Datagrid
              bulkActionButtons={false}
              rowClick={false}
            >
              <TextField
                source="nameBg"
                label="Име на модула"
                sortable={false}
              />
              <ReferenceField
                source="academicYearId"
                reference={studentRequests.getAcademicYearsSelect()}
                label="Академична година"
                sortable={false}
              >
                <TextField source="name" />
              </ReferenceField>
              <ReferenceField
                source="departmentId"
                reference={administrationRequests.getDepartmentsSelect()}
                label="Катедра"
                sortable={false}
              >
                <TextField source="fullName" />
              </ReferenceField>
              <DateField
                source="durationFrom"
                label="Период от"
                sortable={false}
                locales="bg-BG"
              />
              <DateField
                source="durationTo"
                label="Период до"
                sortable={false}
                locales="bg-BG"
              />
              <NumberField
                source="studentCapacity"
                label="Капацитет"
                sortable={false}
              />
              <FunctionField
                label="Форма на оценяване"
                render={record => gradingTypeName(record?.gradingType)}
              />
              <SecuredComponent permission={permissions.canManageElectiveCourses}>
                <EditButton />
              </SecuredComponent>
              <ShowButton />
            </Datagrid>
          )}
        </List>
      )}
    </Box>
  );
};

export default ListElectiveCourses;
